import { useEffect, useRef, useState } from 'react'
import { parseAmount, formatAmountForInput, roundToCents } from '@dcp/shared'
import { Input } from '@atrvd/saas-ui'

/**
 * Props for AmountInput.
 *
 * Designed for use with react-hook-form `Controller`: the parent Controller
 * owns the numeric value and exposes `value` / `onChange`; this component
 * owns only the raw text the user sees while typing.
 */
interface AmountInputProps {
  /** Controlled numeric value from RHF — `undefined` = empty, `NaN` = invalid. */
  value: number | undefined
  /**
   * Called whenever the text changes.
   * - Empty text          → `undefined`   (no value entered)
   * - Parseable text      → numeric value (e.g. `parseAmount("500,500.00")` → 500500)
   * - Unparseable text    → `NaN`         (non-empty but not a valid amount;
   *                                        triggers `invalid_type_error` on z.number())
   */
  onChange: (value: number | undefined) => void
  /** Called when the input loses focus (forwarded from Controller's field). */
  onBlur?: () => void
  id?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean | 'true' | 'false'
}

/**
 * Locale-stable amount text input.
 *
 * Fixes DCP-224 (comma mis-parsed to 500.5 instead of 500500) and DCP-225
 * ("129404,6" shown on a comma-decimal OS locale) by:
 *   - Never using `type="number"` / `valueAsNumber` (browser locale-dependant).
 *   - Parsing the raw text through `parseAmount` from `@dcp/shared` (same parser
 *     the file importers use — locale-stable US-only format: comma = thousands,
 *     dot = decimal).
 *   - Formatting the pre-filled numeric value through `formatAmountForInput`
 *     (dot-decimal, no thousands separator, always 2 decimal places).
 *   - Keeping an internal `displayText` state that holds exactly what the user
 *     typed so mid-typing rewrites / cursor jumps don't occur.
 *
 * Sync rules:
 *   - When `value` changes from outside AND the input is not focused:
 *     reformat to `formatAmountForInput(value)` (or `''` for undefined/NaN).
 *   - While focused: never clobber the user's in-progress typing.
 *   - On blur: reformat to the canonical 2-decimal representation when `value`
 *     is a finite number; leave the display unchanged for empty or NaN.
 */
export function AmountInput({
  value,
  onChange,
  onBlur,
  id,
  placeholder,
  disabled,
  className,
  ...ariaProps
}: AmountInputProps) {
  /**
   * The string the user sees in the input.
   * Initialise from the incoming value so an Edit form that opens with a
   * pre-filled numeric value shows it formatted correctly on first render.
   */
  const [displayText, setDisplayText] = useState<string>(() => {
    if (value === undefined || value === null || !Number.isFinite(value)) return ''
    return formatAmountForInput(value)
  })

  /** Tracks whether this input is currently focused so we don't clobber typing. */
  const isFocusedRef = useRef(false)

  /**
   * Sync the display text when the external `value` prop changes while the
   * input is NOT focused (e.g. the parent form calls `reset()` or the Edit
   * dialog opens with a different record).
   *
   * When the input IS focused, we intentionally skip the sync so the user's
   * in-progress text isn't overwritten by a React re-render triggered from the
   * parent (RHF watches can fire during typing).
   */
  useEffect(() => {
    if (isFocusedRef.current) return
    if (value === undefined || value === null || !Number.isFinite(value)) {
      setDisplayText('')
    } else {
      setDisplayText(formatAmountForInput(value))
    }
  }, [value])

  /**
   * Handle raw text change.
   * Keeps the display string as typed (no mid-type reformat) and fires
   * `onChange` with the parsed numeric value so RHF validation sees a number.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setDisplayText(raw)

    const trimmed = raw.trim()
    if (trimmed === '') {
      onChange(undefined)
      return
    }
    const parsed = parseAmount(trimmed)
    // null from parseAmount means "non-empty but not a valid amount".
    // Pass NaN so that z.number({ invalid_type_error: '...' }) triggers its
    // invalid_type_error message, matching the previous `valueAsNumber` behaviour
    // (NaN from an empty type="number" input triggered the same path).
    //
    // Canonicalize the emitted value to 2 decimals (cents) so the model always
    // equals what is displayed/stored (DCP-307 AC4) — the underlying column is
    // NUMERIC(15,2), so e.g. "1234.5678" must reach form state as 1234.57, not
    // 1234.5678. `displayText` above intentionally keeps the raw typed text
    // untouched (the focus guard depends on it matching what the user typed);
    // only the emitted numeric value is rounded.
    onChange(parsed === null ? NaN : roundToCents(parsed))
  }

  /**
   * On blur: reformat to the canonical 2-decimal dot-decimal string when the
   * value is a finite number (nice, consistent display after typing finishes).
   * Leaves the display unchanged for empty or invalid inputs so the user can
   * see what they typed (the validation error message handles feedback).
   */
  const handleBlur = () => {
    isFocusedRef.current = false
    if (value !== undefined && Number.isFinite(value)) {
      setDisplayText(formatAmountForInput(value))
    }
    onBlur?.()
  }

  const handleFocus = () => {
    isFocusedRef.current = true
  }

  return (
    <Input
      type="text"
      inputMode="decimal"
      id={id}
      value={displayText}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      {...ariaProps}
    />
  )
}
